import React, {useCallback, useEffect, useState} from 'react';
import {
  Close as CloseIcon,
  ChevronLeft as ScrollLeftIcon,
  ChevronRight as ScrollRightIcon
} from '@mui/icons-material';
import classNames from 'classnames';
import {registerGlobalStyle} from '../../theme';
import {BodyText} from '../Text';
import {HoopsPropTypes} from '../utils';
import {ImagePreview} from './ImagePreview';
import {DocumentPortal} from '../Layout';

registerGlobalStyle('.image-gallery-overlay', (theme) => ({
  position: 'absolute',
  pointerEvents: 'none',
  opacity: 0,
  transition: theme.transitions.out.opacity,
  zIndex: theme.zIndex.imageZoom,
  '.image-gallery-container': {
    position: 'fixed',
    height: 'auto',
    maxWidth: '100vw',
    maxHeight: '100vh',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: theme.colors.background.white,
    'img, canvas': {
      objectFit: 'contain',
      height: 'auto',
      maxWidth: '80vw',
      maxHeight: '100vh',
    },
    'canvas': {maxHeight: '90vh',},
    '.previous-button, .next-button': {
      fontSize: 'xx-large',
      position: 'absolute',
      zIndex: theme.zIndex.toolbox,
      width: 70,
      height: '70vh'
    },
    '.previous-button': {left: 0,},
    '.next-button': {right: 0,},
    '.close-icon': {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1),
      fontSize: 60,
      padding: theme.spacing(1.5)
    },
    '.image-count': {
      position: 'absolute',
      top: 0,
      left: 0,
      padding: theme.spacing(3),
      fontSize: 18,
      '.text.text-body': {width: 100}
    },
  },
  '&.gallery-visible': {
    opacity: 1,
    transition: theme.transitions.in.opacity,
    '.image-gallery-container, .next-button, .previous-button, .close-icon': {pointerEvents: 'all'}
  },
}));

export const ImageGalleryOverlay = ({className, image, images, onClose}) => {
  const [activeImage, setActiveImage] = useState(image ?? images[0] ?? null);
  const [isVisible, setIsVisible] = useState(false);

  // This allows the overlay to fade in
  useEffect(() => {
    setIsVisible(true);
  }, [images]);

  const nextImage = useCallback((e) => {
    e.stopPropagation();
    const imageIndex = images.findIndex((index) => index === activeImage);
    setActiveImage(images[images.length - 1 === imageIndex ? 0 : imageIndex + 1]);
  }, [images, activeImage, setActiveImage]);

  const previousImage = useCallback((e) => {
    e.stopPropagation();
    const imageIndex = images.findIndex((index) => index === activeImage);
    setActiveImage(images[imageIndex === 0 ? images.length - 1 : imageIndex - 1]);
  }, [images, activeImage, setActiveImage]);

  return (
    <DocumentPortal>
      <div className={classNames([className, 'image-gallery-overlay', isVisible && 'gallery-visible'])}>
        <div className={classNames(['image-gallery-container'])} onClick={onClose}>
          {images?.length > 1 &&
            <BodyText text={`${images.indexOf(activeImage) + 1} / ${images.length}`} className={'image-count'} />
          }
          <CloseIcon className={'close-icon'} onClick={onClose} />
          <ImagePreview
            className={'zoom-cursor'}
            imageUrl={activeImage}
            onClick={onClose}
            setByContainerHeight
            showPDFPagination
          />
          {images?.length > 1 &&
            <>
              <ScrollLeftIcon className={'previous-button'} onClick={previousImage} />
              <ScrollRightIcon className={'next-button'} onClick={nextImage} />
            </>
          }
        </div>
      </div>
    </DocumentPortal>
  );
};

ImageGalleryOverlay.propTypes = {
  className: HoopsPropTypes.className,
  images: HoopsPropTypes.arrayOfString,
  onClose: HoopsPropTypes.func
};
